import { apiRequest } from "@services/apiClient";

/**
 * A clinic's synced providers, as the admin clinic drawer manages them:
 * role, default, working days. These are the admin-only clinic endpoints;
 * the campaign start modal keeps reading services/reactivation.fetchProviders.
 *
 * Row shape: { external_provider_id, name, is_default, role ("dentist" |
 * "hygienist" | null), role_source ("pms" | "manual" | null), specialty,
 * status, manual_schedule, manual_schedule_updated_at, learned_schedule,
 * schedule_source ("manual" | "learned" | "none") }.
 */

const base = (clinicId) => `/scheduling/clinics/${encodeURIComponent(clinicId)}/providers`;
const one = (clinicId, externalProviderId) =>
  `${base(clinicId)}/${encodeURIComponent(externalProviderId)}`;

/** Every provider of the clinic. Always an array. */
export async function fetchClinicProviders(clinicId) {
  const response = await apiRequest({ method: "GET", endpoint: base(clinicId) });
  return Array.isArray(response?.data) ? response.data : [];
}

/**
 * Set the role ("dentist" | "hygienist" | null for unassigned), or with
 * `reset: true` hand it back to what the PMS type says. Resolves to the
 * updated row, or null.
 */
export async function setClinicProviderRole({ clinicId, externalProviderId, role, reset = false }) {
  const response = await apiRequest({
    method: "PUT",
    endpoint: `${one(clinicId, externalProviderId)}/role`,
    body: reset ? { reset: true } : { role },
  });
  return response?.data?.provider ?? null;
}

/** Make one provider the clinic's default. Resolves to the new list. */
export async function setClinicDefaultProvider({ clinicId, externalProviderId }) {
  const response = await apiRequest({
    method: "PUT",
    endpoint: `${base(clinicId)}/default`,
    body: { external_provider_id: externalProviderId },
  });
  return Array.isArray(response?.data) ? response.data : [];
}

/**
 * Store the rota an admin entered for one provider, or clear it with
 * `weekdays: null` so the provider falls back to the learned pattern.
 * Resolves to the updated row, or null.
 */
export async function setClinicProviderSchedule({ clinicId, externalProviderId, weekdays }) {
  const response = await apiRequest({
    method: "PUT",
    endpoint: `${one(clinicId, externalProviderId)}/schedule`,
    body: { weekdays },
  });
  return response?.data?.provider ?? null;
}
